import dayjs from "dayjs";
import ScheduleModal from "../ScheduleModal/ScheduleModal";
import { useTranslation } from "@coworker/locales";
import CalendarIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/CalendarIconPath";
import { useCallback, useState } from "react";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import {
  calcIsFifthWeekdayOfMonth,
  calcIsLastWeekdayOfMonth,
  getScheduledTaskDescription,
  getWeekdayNumber,
  ScheduleType,
} from "../helper";
import { isPositiveInteger } from "@coworker/apprestructured/src/shared/helpers/string.helper";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import {
  MonthlyRecurrenceType,
  Recurrence,
  RecurrencePeriod,
  WeeklyRecurrenceDay,
} from "@coworker/apprestructured/src/task-management/types/Schedule";

export type CreateTaskScheduleChangeEvent = {
  futureStart: string;
  repeatingStart: string;
  end: string;
  recurrence: Recurrence | null;
};

type CreateTaskScheduleProps = {
  disabled: boolean;
  futureStart: string;
  repeatingStart: string;
  end: string;
  recurrence: Recurrence | null;
  onChange: ({
    futureStart,
    repeatingStart,
    end,
    recurrence,
  }: CreateTaskScheduleChangeEvent) => void;
};

function CreateTaskSchedule(props: CreateTaskScheduleProps) {
  const [showModal, setShowModal] = useState(false);
  const [modalFutureStart, setModalFutureStart] = useState(props.futureStart);
  const [modalRepeatingStart, setModalRepeatingStart] = useState(
    props.repeatingStart
  );
  const [modalEnd, setModalEnd] = useState(props.end);
  const [scheduleType, setScheduleType] = useState<ScheduleType>(
    ScheduleType.NO_REPEAT
  );
  const [recurrencePeriod, setRecurrencePeriod] =
    useState<RecurrencePeriod | null>(props.recurrence?.period || null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [weeklyIntervalError, setWeeklyIntervalError] = useState<string | null>(
    null
  );
  const [monthlyIntervalError, setMonthlyIntervalError] = useState<
    string | null
  >(null);
  const [isRecurrencePeriodValid, setIsReccurencePeriodValid] = useState(true);
  const [modalMonthlyInterval, setModalMonthlyInterval] = useState(
    props.recurrence?.period === "monthly"
      ? String(props.recurrence.interval)
      : "1"
  );
  const [modalMonthlyRecurrenceType, setModalMonthlyRecurrenceType] =
    useState<MonthlyRecurrenceType>(
      props.recurrence?.period === "monthly" ? props.recurrence.type : "weekday"
    );
  const [modalWeeklyInterval, setModalWeeklyInterval] = useState(
    props.recurrence?.period === "weekly"
      ? String(props.recurrence.interval)
      : "1"
  );
  const [modalWeeklyRecurrenceDays, setModalWeeklyRecurrenceDays] = useState<
    WeeklyRecurrenceDay[]
  >(props.recurrence?.period === "weekly" ? props.recurrence.days : []);

  const { t } = useTranslation();

  const handleListViewItemClick = useCallback(() => {
    if (props.futureStart) {
      setScheduleType(ScheduleType.FUTURE);
    } else if (props.recurrence) {
      setScheduleType(ScheduleType.REPEATING);
    } else {
      setScheduleType(ScheduleType.NO_REPEAT);
    }
    setModalFutureStart(props.futureStart);
    setModalRepeatingStart(props.repeatingStart);
    setModalEnd(props.end);
    setRecurrencePeriod(props.recurrence?.period || null);
    setModalMonthlyInterval(
      props.recurrence?.period === "monthly"
        ? String(props.recurrence.interval)
        : "1"
    );
    setModalMonthlyRecurrenceType(
      props.recurrence?.period === "monthly" ? props.recurrence.type : "weekday"
    );
    setModalWeeklyInterval(
      props.recurrence?.period === "weekly"
        ? String(props.recurrence.interval)
        : "1"
    );
    setModalWeeklyRecurrenceDays(
      props.recurrence?.period === "weekly" ? props.recurrence.days : []
    );
    setStartDateError(null);
    setEndDateError(null);
    setWeeklyIntervalError(null);
    setMonthlyIntervalError(null);
    setIsReccurencePeriodValid(true);
    setShowModal(true);
  }, [props]);

  const closeAndResetModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(closeAndResetModal);

  const handleConfirmClick = useCallback(() => {
    let hasError = false;
    if (scheduleType === ScheduleType.FUTURE) {
      const startError = !modalFutureStart ? t("pleaseSelectDateString") : null;

      setStartDateError(startError);

      hasError = hasError || !!startError;
    } else if (scheduleType === ScheduleType.REPEATING) {
      const startError = !modalRepeatingStart
        ? t("pleaseSelectDateString")
        : null;
      const endError = !modalEnd
        ? t("pleaseSelectDateString")
        : modalEnd <= modalRepeatingStart
          ? t("endDateMustBeAfterStartDateString")
          : dayjs(modalEnd).diff(dayjs(modalRepeatingStart), "year") > 0
            ? t("endDateMustBeWithinOneYearString")
            : null;

      setStartDateError(startError);
      setEndDateError(endError);
      hasError = hasError || !!startError || !!endError;

      if (!recurrencePeriod) {
        setIsReccurencePeriodValid(false);
        hasError = true;
      } else if (
        recurrencePeriod === "weekly" &&
        !isPositiveInteger(modalWeeklyInterval)
      ) {
        setWeeklyIntervalError(t("errorValidNumberString"));
        hasError = true;
      } else if (
        recurrencePeriod === "monthly" &&
        !isPositiveInteger(modalMonthlyInterval)
      ) {
        setMonthlyIntervalError(t("errorValidNumberString"));
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    closeAndResetModal();
    disableSaveGuard();

    if (scheduleType === ScheduleType.NO_REPEAT) {
      props.onChange({
        futureStart: "",
        repeatingStart: "",
        end: "",
        recurrence: null,
      });
      return;
    }

    if (scheduleType === ScheduleType.FUTURE) {
      props.onChange({
        futureStart: modalFutureStart,
        repeatingStart: "",
        end: "",
        recurrence: null,
      });
      return;
    }

    if (scheduleType === ScheduleType.REPEATING) {
      switch (recurrencePeriod) {
        case "weekly":
          props.onChange({
            futureStart: "",
            repeatingStart: modalRepeatingStart,
            end: modalEnd,
            recurrence: {
              period: "weekly",
              interval: parseInt(modalWeeklyInterval),
              days: modalWeeklyRecurrenceDays,
            },
          });
          return;

        case "monthly":
          props.onChange({
            futureStart: "",
            repeatingStart: modalRepeatingStart,
            end: modalEnd,
            recurrence: {
              period: "monthly",
              interval: parseInt(modalMonthlyInterval),
              type: modalMonthlyRecurrenceType,
            },
          });
          return;

        case "daily":
          props.onChange({
            futureStart: "",
            repeatingStart: modalRepeatingStart,
            end: modalEnd,
            recurrence: {
              period: "daily",
            },
          });
          return;
      }
    }
  }, [
    scheduleType,
    closeAndResetModal,
    modalFutureStart,
    t,
    modalRepeatingStart,
    modalEnd,
    recurrencePeriod,
    props,
    modalWeeklyInterval,
    modalWeeklyRecurrenceDays,
    modalMonthlyInterval,
    modalMonthlyRecurrenceType,
    disableSaveGuard,
  ]);

  const handleScheduleTypeChange = useCallback(
    (type: ScheduleType) => {
      setScheduleType(type);
      setStartDateError(null);
      setEndDateError(null);
      setIsReccurencePeriodValid(true);
      setWeeklyIntervalError(null);
      setMonthlyIntervalError(null);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleFutureStartChange = useCallback(
    (value: string) => {
      setStartDateError(null);
      setModalFutureStart(value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleRepeatingStartChange = useCallback(
    (value: string) => {
      setStartDateError(null);
      setModalRepeatingStart(value);
      if (value) {
        setModalWeeklyRecurrenceDays([getWeekdayNumber(value)]);
        const isFifthWeekdayOfMonth = calcIsFifthWeekdayOfMonth(value);
        const isLastWeekdayOfMonth = calcIsLastWeekdayOfMonth(value);
        if (isFifthWeekdayOfMonth) {
          setModalMonthlyRecurrenceType("last_weekday");
        } else if (!isLastWeekdayOfMonth) {
          setModalMonthlyRecurrenceType("weekday");
        }
      } else {
        setModalWeeklyRecurrenceDays([]);
        setModalMonthlyRecurrenceType("weekday");
      }
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleEndChange = useCallback(
    (value: string) => {
      setEndDateError(null);
      setModalEnd(value);
      enableSaveGuard();
    },
    [setModalEnd, enableSaveGuard]
  );

  const handleMonthlyIntervalChange = useCallback(
    (value: string) => {
      setMonthlyIntervalError(null);
      setModalMonthlyInterval(value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleMonthlyRecurrenceTypeChange = useCallback(
    (value: MonthlyRecurrenceType) => {
      setModalMonthlyRecurrenceType(value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleWeeklyIntervalChange = useCallback(
    (value: string) => {
      setWeeklyIntervalError(null);
      setModalWeeklyInterval(value);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleRecurrencePeriodChange = useCallback(
    (value: RecurrencePeriod) => {
      setRecurrencePeriod(value);
      setStartDateError(null);
      setEndDateError(null);
      setIsReccurencePeriodValid(true);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleWeeklyRecurrenceDaysChange = useCallback(
    (days: WeeklyRecurrenceDay[]) => {
      setModalWeeklyRecurrenceDays(days);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  return (
    <>
      <ListItem
        title={t("scheduleTaskString")}
        icon={CalendarIconPath}
        onClick={handleListViewItemClick}
        disabled={props.disabled}
        emphasised={
          !!props.futureStart || !!props.repeatingStart || !!props.recurrence
        }
        description={
          getScheduledTaskDescription(
            props.futureStart,
            props.repeatingStart,
            props.recurrence,
            t
          ) || ""
        }
      />
      <ScheduleModal
        showModal={showModal}
        scheduleType={scheduleType}
        futureStart={modalFutureStart}
        isRecurrencePeriodValid={isRecurrencePeriodValid}
        recurrencePeriod={recurrencePeriod}
        repeatingStart={modalRepeatingStart}
        end={modalEnd}
        startDateError={startDateError}
        endDateError={endDateError}
        monthlyInterval={modalMonthlyInterval}
        monthlyRecurrenceType={modalMonthlyRecurrenceType}
        weeklyInterval={modalWeeklyInterval}
        weeklyRecurrenceDays={modalWeeklyRecurrenceDays}
        onScheduleTypeChange={handleScheduleTypeChange}
        onFutureStartChange={handleFutureStartChange}
        onRecurrencePeriodChange={handleRecurrencePeriodChange}
        onRepeatingStartChange={handleRepeatingStartChange}
        onEndChange={handleEndChange}
        onConfirmClick={handleConfirmClick}
        onCloseModal={handleCloseClick}
        onMonthlyIntervalChange={handleMonthlyIntervalChange}
        onMonthlyRecurrenceTypeChange={handleMonthlyRecurrenceTypeChange}
        onWeeklyIntervalChange={handleWeeklyIntervalChange}
        onWeeklyRecurrenceDaysChange={handleWeeklyRecurrenceDaysChange}
        weeklyIntervalError={weeklyIntervalError}
        monthlyIntervalError={monthlyIntervalError}
      />
    </>
  );
}

export default CreateTaskSchedule;
